import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Paper, 
  Box, 
  Card,
  CardHeader,
  CardContent,
  Grid,
  Tab,
  Tabs,
  Button,
  Stack,
  CircularProgress
} from '@mui/material';
import {
  Architecture as ArchitectureIcon,
  Code as CodeIcon,
  Functions as FunctionsIcon,
  AutoStories as AutoStoriesIcon,
  Timer as TimerIcon,
  Category as CategoryIcon,
  Splitscreen as SplitscreenIcon,
  Search as SearchIcon,
  Build as BuildIcon,
  BugReport as BugReportIcon,
  TrendingUp as TrendingUpIcon,
  Description as DescriptionIcon,
  Notes as NotesIcon,
  Block as BlockIcon,
  Layers as LayersIcon,
  Lightbulb as LightbulbIcon,
  LightbulbOutlined,
  Storage as StorageIcon,
  Public as PublicIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { marked } from 'marked';
import hljs from 'highlight.js';
import 'highlight.js/styles/github.css';
import { fetchQuestionDetail } from '../services/questionService';
import GlossaryTerm from '../components/common/GlossaryTerm';
import PracticeEntry from '../components/practice/PracticeEntry';

// Configure marked with syntax highlighting
marked.setOptions({
  gfm: true,
  breaks: true,
  highlight: function(code, lang) {
    if (lang && hljs.getLanguage(lang)) {
      try {
        return hljs.highlight(code, { language: lang }).value;
      } catch (err) {
        console.error('Error highlighting code:', err);
      }
    }
    return code;
  }
});

// Parse the question content from markdown
const parseQuestionContent = (content) => {
  const sections = {
    metadata: {
      id: '',
      title: '',
      difficulty: '',
      category: '',
      subcategory: '',
      similarQuestions: '',
      realLifeDomains: ''
    },
    problemDescription: '',
    versions: [],
    notes: '',
    constraints: ''
  };

  // Split content by sections
  const lines = content.split('\n');
  let currentSection = '';
  let versionBuffer = { title: '', content: '' };
  let inVersions = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // Check for main section headers
    if (line.startsWith('# ')) {
      const sectionTitle = line.slice(2).trim().toLowerCase();
      if (sectionTitle === 'metadata') {
        currentSection = 'metadata';
      } else if (sectionTitle === 'problem description') {
        currentSection = 'problemDescription';
      } else if (sectionTitle === 'versions') {
        currentSection = 'versions';
        inVersions = true;
      } else if (sectionTitle === 'notes') {
        currentSection = 'notes';
      } else if (sectionTitle === 'constraints') {
        currentSection = 'constraints';
      }
      continue;
    }

    // Handle metadata
    if (currentSection === 'metadata' && line.startsWith('- **')) {
      // Match the exact format from the question bank file
      const match = line.match(/- \*\*(.*?)\*\*: (.*)/);
      if (match) {
        const [, key, value] = match;
        // Map the metadata keys to the correct property names
        const keyMap = {
          'id': 'id',
          'title': 'title',
          'difficulty': 'difficulty',
          'category': 'category',
          'subcategory': 'subcategory',
          'similar questions': 'similarQuestions',
          'real life domains': 'realLifeDomains'
        };
        const normalizedKey = keyMap[key.toLowerCase()];
        if (normalizedKey && sections.metadata.hasOwnProperty(normalizedKey)) {
          // Handle Real Life Domains special case - it ends with a colon
          let cleanValue = value.trim();
          if (cleanValue.endsWith(':')) {
            cleanValue = cleanValue.slice(0, -1).trim();
          }
          sections.metadata[normalizedKey] = cleanValue;
        }
      }
      continue;
    }

    // Handle version subsections
    if (inVersions && line.startsWith('## ')) {
      if (versionBuffer.content) {
        sections.versions.push({ ...versionBuffer });
      }
      versionBuffer = {
        title: line.slice(3).trim(),
        content: ''
      };
      continue;
    }

    // Add content to appropriate section
    if (currentSection === 'problemDescription') {
      sections.problemDescription += line + '\n';
    } else if (currentSection === 'notes') {
      sections.notes += line + '\n';
    } else if (currentSection === 'constraints') {
      sections.constraints += line + '\n';
    } else if (inVersions && versionBuffer.title) {
      versionBuffer.content += line + '\n';
    }
  }

  // Add the last version if exists
  if (inVersions && versionBuffer.content) {
    sections.versions.push({ ...versionBuffer });
  }

  // Clean up the content
  sections.problemDescription = sections.problemDescription.trim();
  sections.notes = sections.notes.trim();
  sections.constraints = sections.constraints.trim();
  sections.versions = sections.versions.map(v => ({
    ...v,
    content: v.content.trim()
  }));

  return sections;
};

// Parse solution content from solution bank
const parseSolutionContent = (content) => {
  const sections = {
    classificationRationale: '',
    solveFramework: {
      split: '',
      observe: '',
      look: '',
      verify: '',
      evolve: ''
    },
    howApproach: {
      pythonicImplementation: '',
      mathematicalAbstraction: '',
      realWorldAnalogies: '',
      storytellingApproach: '',
      visualRepresentation: ''
    },
    complexityAnalysis: {
      time: '',
      space: ''
    },
    notes: '',
    constraints: ''
  };

  let currentMainSection = '';
  let currentSubSection = '';
  let currentContent = [];

  const processContent = (content) => {
    if (!content) return '';
    return content
      .replace(/^#[^#\n]*\n/, '')  // Remove h1 headers
      .replace(/^##[^#\n]*\n/, '') // Remove h2 headers
      .split('```').map((part, i) => 
        i % 2 === 0 ? part.trim() : part // Preserve code blocks
      ).join('```')
      .trim();
  };

  content.split('\n').forEach((line) => {
    // Main section detection
    if (line.match(/^# Classification Rationale/i)) {
      if (currentMainSection === 'solveFramework' && currentSubSection) {
        sections.solveFramework[currentSubSection] = processContent(currentContent.join('\n'));
      } else if (currentMainSection === 'howApproach' && currentSubSection) {
        sections.howApproach[currentSubSection] = processContent(currentContent.join('\n'));
      } else if (currentMainSection === 'complexityAnalysis' && currentSubSection) {
        sections.complexityAnalysis[currentSubSection] = processContent(currentContent.join('\n'));
      } else if (currentMainSection) {
        sections[currentMainSection] = processContent(currentContent.join('\n'));
      }
      currentMainSection = 'classificationRationale';
      currentSubSection = '';
      currentContent = [];
    }
    else if (line.match(/^# SOLVE Framework/i)) {
      if (currentMainSection) {
        sections[currentMainSection] = processContent(currentContent.join('\n'));
      }
      currentMainSection = 'solveFramework';
      currentSubSection = '';
      currentContent = [];
    }
    // SOLVE Framework subsection detection
    else if (currentMainSection === 'solveFramework' && line.match(/^## S - Split the Problem/i)) {
      if (currentSubSection) {
        sections.solveFramework[currentSubSection] = processContent(currentContent.join('\n'));
      }
      currentSubSection = 'split';
      currentContent = [];
    }
    else if (currentMainSection === 'solveFramework' && line.match(/^## O - Observe Examples/i)) {
      if (currentSubSection) {
        sections.solveFramework[currentSubSection] = processContent(currentContent.join('\n'));
      }
      currentSubSection = 'observe';
      currentContent = [];
    }
    else if (currentMainSection === 'solveFramework' && line.match(/^## L - Look for Tools/i)) {
      if (currentSubSection) {
        sections.solveFramework[currentSubSection] = processContent(currentContent.join('\n'));
      }
      currentSubSection = 'look';
      currentContent = [];
    }
    else if (currentMainSection === 'solveFramework' && line.match(/^## V - Verify Edge Cases/i)) {
      if (currentSubSection) {
        sections.solveFramework[currentSubSection] = processContent(currentContent.join('\n'));
      }
      currentSubSection = 'verify';
      currentContent = [];
    }
    else if (currentMainSection === 'solveFramework' && line.match(/^## E - Evolve Your Solution/i)) {
      if (currentSubSection) {
        sections.solveFramework[currentSubSection] = processContent(currentContent.join('\n'));
      }
      currentSubSection = 'evolve';
      currentContent = [];
    }
    // 5 HOW Approach sections
    else if (line.match(/^# Pythonic Implementation/i)) {
      if (currentMainSection === 'solveFramework' && currentSubSection) {
        sections.solveFramework[currentSubSection] = processContent(currentContent.join('\n'));
      } else if (currentMainSection === 'howApproach' && currentSubSection) {
        sections.howApproach[currentSubSection] = processContent(currentContent.join('\n'));
      }
      currentMainSection = 'howApproach';
      currentSubSection = 'pythonicImplementation';
      currentContent = [];
    }
    else if (line.match(/^# Mathematical Abstraction/i)) {
      if (currentMainSection === 'howApproach' && currentSubSection) {
        sections.howApproach[currentSubSection] = processContent(currentContent.join('\n'));
      }
      currentSubSection = 'mathematicalAbstraction';
      currentContent = [];
    }
    else if (line.match(/^# Real World Analogies/i)) {
      if (currentMainSection === 'howApproach' && currentSubSection) {
        sections.howApproach[currentSubSection] = processContent(currentContent.join('\n'));
      }
      currentSubSection = 'realWorldAnalogies';
      currentContent = [];
    }
    else if (line.match(/^# Storytelling Approach/i)) {
      if (currentMainSection === 'howApproach' && currentSubSection) {
        sections.howApproach[currentSubSection] = processContent(currentContent.join('\n'));
      }
      currentSubSection = 'storytellingApproach';
      currentContent = [];
    }
    else if (line.match(/^# Visual Representation/i)) {
      if (currentMainSection === 'howApproach' && currentSubSection) {
        sections.howApproach[currentSubSection] = processContent(currentContent.join('\n'));
      }
      currentSubSection = 'visualRepresentation';
      currentContent = [];
    }
    // Complexity Analysis sections
    else if (line.match(/^# Complexity Analysis/i)) {
      if (currentMainSection === 'howApproach' && currentSubSection) {
        sections.howApproach[currentSubSection] = processContent(currentContent.join('\n'));
      }
      currentMainSection = 'complexityAnalysis';
      currentSubSection = '';
      currentContent = [];
    }
    else if (line.match(/^## Time Complexity/i)) {
      if (currentMainSection === 'complexityAnalysis') {
        currentSubSection = 'time';
        currentContent = [];
      }
    }
    else if (line.match(/^## Space Complexity/i)) {
      if (currentMainSection === 'complexityAnalysis') {
        if (currentSubSection === 'time') {
          sections.complexityAnalysis.time = processContent(currentContent.join('\n'));
        }
        currentSubSection = 'space';
        currentContent = [];
      }
    }
    // Add line to current section
    else if (currentMainSection) {
      currentContent.push(line);
    }
  });

  // Save the last section
  if (currentMainSection === 'howApproach' && currentSubSection) {
    sections.howApproach[currentSubSection] = processContent(currentContent.join('\n'));
  } else if (currentMainSection === 'complexityAnalysis' && currentSubSection) {
    sections.complexityAnalysis[currentSubSection] = processContent(currentContent.join('\n'));
  } else if (currentMainSection === 'solveFramework' && currentSubSection) {
    sections.solveFramework[currentSubSection] = processContent(currentContent.join('\n'));
  } else if (currentMainSection) {
    sections[currentMainSection] = processContent(currentContent.join('\n'));
  }

  return sections;
};

// Problem section component
const ProblemSection = ({ metadata, description, versions, notes, constraints }) => {
  return (
    <Grid container spacing={3}>
      {/* Top Row: Metadata and Description */}
      <Grid item xs={12} md={4}>
        <Card sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            title="Problem Information"
            avatar={<CategoryIcon />}
          />
          <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="primary" gutterBottom>
                  <strong>ID & Title</strong>
                </Typography>
                <Typography paragraph>{metadata?.id} - {metadata?.title}</Typography>

                <Typography variant="subtitle1" color="primary" gutterBottom>
                  <strong>Category</strong>
                </Typography>
                <Typography paragraph>{metadata?.category}</Typography>

                <Typography variant="subtitle1" color="primary" gutterBottom>
                  <strong>Subcategory</strong>
                </Typography>
                <Typography paragraph>{metadata?.subcategory}</Typography>

                <Typography variant="subtitle1" color="primary" gutterBottom>
                  <strong>Difficulty</strong>
                </Typography>
                <Typography paragraph>{metadata?.difficulty}</Typography>

                <Typography variant="subtitle1" color="primary" gutterBottom>
                  <strong>Similar topics</strong>
                </Typography>
                <Typography paragraph>{metadata?.similarQuestions}</Typography>

                <Typography variant="subtitle1" color="primary" gutterBottom>
                  <strong>Where this comes up in real jobs</strong>
                </Typography>
                <Typography>{metadata?.realLifeDomains}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card sx={{ height: '400px', display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            title="Problem Description"
            avatar={<DescriptionIcon />}
          />
          <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
            <div dangerouslySetInnerHTML={{ __html: marked(description || '') }} />
          </CardContent>
        </Card>
      </Grid>

      {/* Versions Grid */}
      <Grid item xs={12}>
        <Card sx={{ display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            title="Problem Versions"
            avatar={<LayersIcon />}
          />
          <CardContent>
            <Grid container spacing={2}>
              {versions?.map((version, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Paper 
                    elevation={2} 
                    sx={{ 
                      p: 2, 
                      mb: 2, 
                      height: '300px',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Typography variant="h6" color="primary" gutterBottom>
                      {version.title}
                    </Typography>
                    <Box sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
                      <div dangerouslySetInnerHTML={{ __html: marked(version.content || '') }} />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Bottom Row: Notes and Constraints */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: '300px', display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            title="Notes"
            avatar={<NotesIcon />}
          />
          <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
            <div dangerouslySetInnerHTML={{ __html: marked(notes || '') }} />
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ height: '300px', display: 'flex', flexDirection: 'column' }}>
          <CardHeader
            title="Constraints"
            avatar={<BlockIcon />}
          />
          <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
            <div dangerouslySetInnerHTML={{ __html: marked(constraints || '') }} />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// Complexity Analysis section component with side-by-side cards
const ComplexitySection = ({ sections }) => (
  <Grid container spacing={3}>
    <Grid item xs={12} md={6}>
      <Card sx={{ height: '300px', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title="Time Complexity"
          subheader="How much slower this gets as the input grows"
          avatar={<TimerIcon />}
        />
        <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
          <div dangerouslySetInnerHTML={{ __html: marked(sections.time || '') }} />
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} md={6}>
      <Card sx={{ height: '300px', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title="Space Complexity"
          subheader="How much extra memory this uses as the input grows"
          avatar={<StorageIcon />}
        />
        <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
          <div dangerouslySetInnerHTML={{ __html: marked(sections.space || '') }} />
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

// HOW Approach subsection component
const HowApproachSection = ({ sections }) => {
  const [activeSubSection, setActiveSubSection] = useState('pythonicImplementation');
  const theme = useTheme();

  const subSections = [
    {
      id: 'pythonicImplementation',
      label: 'Show how it works',
      icon: <CodeIcon />,
      content: sections.pythonicImplementation,
    },
    {
      id: 'mathematicalAbstraction',
      label: 'Explain the logic',
      icon: <FunctionsIcon />,
      content: sections.mathematicalAbstraction,
    },
    {
      id: 'realWorldAnalogies',
      label: 'Compare to everyday life',
      icon: <PublicIcon />,
      content: sections.realWorldAnalogies,
    },
    {
      id: 'storytellingApproach',
      label: 'Tell the story',
      icon: <AutoStoriesIcon />,
      content: sections.storytellingApproach,
    },
    {
      id: 'visualRepresentation',
      label: 'Draw or describe it',
      icon: <ImageIcon />,
      content: sections.visualRepresentation,
    },
  ];

  return (
    <Box>
      <Stack direction="row" spacing={{ xs: 0, sm: 2 }} sx={{ mb: 3, overflowX: 'auto', '&::-webkit-scrollbar': { height: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
        {subSections.map((section) => (
          <Button
            key={section.id}
            startIcon={section.icon}
            onClick={() => setActiveSubSection(section.id)}
            sx={{
              color: activeSubSection === section.id ? 'primary.main' : 'text.secondary',
              borderBottom: 2,
              borderColor: activeSubSection === section.id ? 'primary.main' : 'transparent',
              borderRadius: 0,
              px: { xs: 1.5, sm: 2 },
              py: 1,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                borderColor: activeSubSection === section.id 
                  ? 'primary.main' 
                  : alpha(theme.palette.primary.main, 0.3),
              },
            }}
          >
            {section.label}
          </Button>
        ))}
      </Stack>
      <Box sx={{ position: 'relative', minHeight: 400 }}>
        {subSections.map((section) => (
          <Box
            key={section.id}
            sx={{
              position: 'absolute',
              width: '100%',
              opacity: activeSubSection === section.id ? 1 : 0,
              visibility: activeSubSection === section.id ? 'visible' : 'hidden',
              transition: 'opacity 0.2s ease-in-out',
            }}
          >
            <Card sx={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
              <CardHeader
                title={section.label}
                avatar={section.icon}
              />
              <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
                <div dangerouslySetInnerHTML={{ __html: marked(section.content || '') }} />
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// Solve Framework section component
const SolveFrameworkSection = ({ sections }) => (
  <>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      <GlossaryTerm term="solve">SOLVE</GlossaryTerm> is a 5-step way to break a problem down before you
      code it — each card below is one step, and the first letters spell the name: <strong>S</strong>plit,{' '}
      <strong>O</strong>bserve, <strong>L</strong>ook, <strong>V</strong>erify, <strong>E</strong>volve.
    </Typography>
    <Grid container spacing={3}>
    <Grid item xs={12} md={6}>
      <Card sx={{ height: '300px', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title="S — Split the Problem"
          avatar={<SplitscreenIcon />}
        />
        <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
          <div dangerouslySetInnerHTML={{ __html: marked(sections.split || '') }} />
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} md={6}>
      <Card sx={{ height: '300px', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title="O — Observe Examples"
          avatar={<SearchIcon />}
        />
        <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
          <div dangerouslySetInnerHTML={{ __html: marked(sections.observe || '') }} />
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12}>
      <Card sx={{ height: '300px', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title="L — Look for Tools"
          avatar={<BuildIcon />}
        />
        <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
          <div dangerouslySetInnerHTML={{ __html: marked(sections.look || '') }} />
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} md={6}>
      <Card sx={{ height: '300px', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title="V — Verify Edge Cases"
          avatar={<BugReportIcon />}
        />
        <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
          <div dangerouslySetInnerHTML={{ __html: marked(sections.verify || '') }} />
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} md={6}>
      <Card sx={{ height: '300px', display: 'flex', flexDirection: 'column' }}>
        <CardHeader
          title="E — Evolve Your Solution"
          avatar={<TrendingUpIcon />}
        />
        <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
          <div dangerouslySetInnerHTML={{ __html: marked(sections.evolve || '') }} />
        </CardContent>
      </Card>
    </Grid>
    </Grid>
  </>
);

// Solution section tabs with reordered sections
const SolutionTabs = ({ sections }) => {
  const [activeTab, setActiveTab] = useState('classification');  // Set classification as default
  const theme = useTheme();

  const tabs = [
    {
      id: 'classification',
      label: 'Why this approach?',
      icon: <CategoryIcon />,
      content: sections.classificationRationale,
      description: 'The thinking behind the solution'
    },
    {
      id: 'solve',
      label: 'SOLVE it step by step',
      icon: <ArchitectureIcon />,
      content: <SolveFrameworkSection sections={sections.solveFramework} />,
      description: 'Break the problem down and solve it'
    },
    {
      id: 'how',
      label: 'Explain it 5 ways',
      icon: <LightbulbOutlined />,
      content: <HowApproachSection sections={sections.howApproach} />,
      description: 'See the solution from different angles'
    },
    {
      id: 'complexity',
      label: 'Speed & memory',
      icon: <TimerIcon />,
      content: <ComplexitySection sections={sections.complexityAnalysis} />,
      description: 'How fast and how much memory it uses'
    },
  ];

  return (
    <Box sx={{ mb: 8 }}>  {/* Add margin bottom to keep content above footer */}
      {/* Tab Buttons */}
      <Stack
        direction="row"
        spacing={{ xs: 0, sm: 1 }}
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: 'divider',
          pb: 1,
          overflowX: 'auto',
          '&::-webkit-scrollbar': { height: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 },
        }}
      >
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            startIcon={tab.icon}
            onClick={() => setActiveTab(tab.id)}
            sx={{
              color: activeTab === tab.id ? 'primary.main' : 'text.secondary',
              borderBottom: 2,
              borderColor: activeTab === tab.id ? 'primary.main' : 'transparent',
              borderRadius: 0,
              px: { xs: 1.5, sm: 3 },
              py: 1,
              flexShrink: 0,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                borderColor: activeTab === tab.id ? 'primary.main' : alpha(theme.palette.primary.main, 0.3),
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Stack alignItems="center">
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  color: activeTab === tab.id ? 'primary.main' : 'text.primary',
                }}
              >
                {tab.label}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  display: { xs: 'none', sm: 'block' }
                }}
              >
                {tab.description}
              </Typography>
            </Stack>
          </Button>
        ))}
      </Stack>

      {/* Tab Content */}
      <Box sx={{ 
        position: 'relative', 
        minHeight: 400,
        mb: 4  // Add margin bottom to content area
      }}>
        {tabs.map((tab) => (
          <Box
            key={tab.id}
            sx={{
              position: 'absolute',
              width: '100%',
              opacity: activeTab === tab.id ? 1 : 0,
              visibility: activeTab === tab.id ? 'visible' : 'hidden',
              transition: 'opacity 0.2s ease-in-out',
            }}
          >
            {typeof tab.content === 'string' ? (
              <Card sx={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
                <CardHeader
                  title={tab.label}
                  avatar={tab.icon}
                />
                <CardContent sx={{ flexGrow: 1, overflow: 'auto', '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 } }}>
                  <div dangerouslySetInnerHTML={{ __html: marked(tab.content || '') }} />
                </CardContent>
              </Card>
            ) : (
              tab.content
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const QuestionPage = () => {
  const { '*': questionPath = '' } = useParams();
  const [sections, setSections] = useState({
    metadata: {
      id: '',
      title: '',
      difficulty: '',
      category: '',
      subcategory: '',
      similarQuestions: '',
      realLifeDomains: ''
    },
    problemDescription: '',
    versions: [],
    notes: '',
    constraints: '',
    solution: {
      classificationRationale: '',
      solveFramework: {
        split: '',
        observe: '',
        look: '',
        verify: '',
        evolve: ''
      },
      howApproach: {
        pythonicImplementation: '',
        mathematicalAbstraction: '',
        realWorldAnalogies: '',
        storytellingApproach: '',
        visualRepresentation: ''
      },
      complexityAnalysis: {
        time: '',
        space: ''
      }
    }
  });

  const [activeSection, setActiveSection] = useState('problem');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        const payload = await fetchQuestionDetail(questionPath);
        const problemContent = payload.content_markdown;
        const solutionContent = payload.solution_markdown || '';
        
        // Parse both contents
        const questionSections = parseQuestionContent(problemContent);
        const solutionSections = parseSolutionContent(solutionContent);
        
        // Set sections
        setSections({
          ...questionSections,
          solution: solutionSections
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching content:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    fetchContent();
  }, [questionPath]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error" align="center">{error}</Typography>
      ) : (
        <>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom sx={{ wordBreak: 'break-word' }}>
              {sections.metadata.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" color="text.secondary">
                {sections.metadata.difficulty}
              </Typography>
            </Box>
            <Box sx={{ mt: 2 }}>
              <PracticeEntry
                item={{
                  sourceType: 'question',
                  sourceKey: questionPath,
                  title: sections.metadata.title || questionPath,
                  prompt: sections.problemDescription || 'Explain how you would understand and approach this question.',
                  context: {
                    question_path: questionPath,
                    category: sections.metadata.category,
                    difficulty: sections.metadata.difficulty
                  },
                  guidance: {
                    solve: {
                      split: sections.solution.solveFramework.split,
                      observe: sections.solution.solveFramework.observe,
                      tools: sections.solution.solveFramework.look,
                      verify: sections.solution.solveFramework.verify,
                      evolve: sections.solution.solveFramework.evolve
                    },
                    'five-how': {
                      practical: sections.solution.howApproach.pythonicImplementation,
                      logic: sections.solution.howApproach.mathematicalAbstraction,
                      analogy: sections.solution.howApproach.realWorldAnalogies,
                      story: sections.solution.howApproach.storytellingApproach,
                      visual: sections.solution.howApproach.visualRepresentation
                    }
                  }
                }}
                compact
              />
            </Box>
          </Box>

          {/* Navigation Tabs */}
          <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeSection}
              onChange={(e, newValue) => setActiveSection(newValue)}
              aria-label="content sections"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab 
                label="Problem" 
                value="problem"
                icon={<DescriptionIcon />}
                iconPosition="start"
              />
              <Tab 
                label="Solution" 
                value="solution"
                icon={<LightbulbIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Box>

          {/* Content Area */}
          <Box sx={{ mt: 3 }}>
            {activeSection === 'problem' ? (
              <ProblemSection 
                metadata={sections.metadata}
                description={sections.problemDescription}
                versions={sections.versions}
                notes={sections.notes}
                constraints={sections.constraints}
              />
            ) : activeSection === 'solution' ? (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <SolutionTabs sections={sections.solution} />
                </Grid>
              </Grid>
            ) : null}
          </Box>
        </>
      )}
    </Container>
  );
};

export default QuestionPage;
